import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useSearchParams } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";

function AuthCallback() {
  const { isLoading, isAuthenticated, error, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorParam = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    // If authenticated and no errors, redirect to intended destination
    if (isAuthenticated && !error && !errorParam) {
      // Get the return URL from Auth0's appState
      getIdTokenClaims()
        .then((claims) => {
          const appState = claims?.appState || {};
          const returnTo = appState.returnTo || searchParams.get("returnTo") || "/";

          setTimeout(() => {
            navigate(returnTo);
          }, 1000);
        })
        .catch(() => {
          // Fallback to home if we can't get claims
          setTimeout(() => {
            navigate("/");
          }, 1000);
        });
    }
  }, [isAuthenticated, error, errorParam, navigate, searchParams, getIdTokenClaims]);

  if (error || errorParam) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Authentication Error
            </CardTitle>
            <CardDescription>There was a problem signing you in</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {errorDescription ||
                  error?.message ||
                  errorParam ||
                  "An unexpected error occurred during authentication"}
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button onClick={() => navigate("/")} variant="outline" className="flex-1">
                Go Home
              </Button>
              <Button onClick={() => window.location.reload()} className="flex-1">
                Try Again
              </Button>
            </div>

            {import.meta.env.DEV && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Debug Information</summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                  {JSON.stringify(
                    {
                      error: errorParam,
                      error_description: errorDescription,
                      auth0_error: error,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Completing authentication...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center space-y-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="text-center">
                <p className="font-medium">Authentication successful!</p>
                <p className="text-sm text-muted-foreground mt-1">Redirecting you...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Processing authentication...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AuthCallback;
